import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IRequestState } from './interfaces';
import {
  GetFirstUnusedRankForLocationAndSysPrinRequest,
  GetLocationsRequest,
  GetLocationsWithDetailsRequest,
  IGetFirstUnusedRankForLocationAndSysPrinRequest,
  IGetFirstUnusedRankForLocationAndSysPrinResponse,
  IGetLocationsRequest,
  IGetLocationsResponse,
  IGetLocationsWithDetailsRequest,
  IGetLocationsWithDetailsResponse,
  ILocationDto,
} from '../api/client';
import Api from '../api/helpers';
import { IDocTypesState } from './docTypesSlice';
import { ICustomersState } from './customersSlice';

export interface ILocationState {
  locations: { locations: ILocationDto[] } & IRequestState;
  locationsWithDetails: { locations: ILocationDto[] } & IRequestState;
  firstUnusedAvailableRank: { firstUnusedAvailableRank: number | null } & IRequestState;
}

const initialState: ILocationState = {
  locations: { locations: [], isRequestActive: false, errorDetails: { statusCode: null, userMessage: null } },
  locationsWithDetails: {
    locations: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  firstUnusedAvailableRank: {
    firstUnusedAvailableRank: null,
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
};

const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchLocations.pending, (state) => {
      state.locations.isRequestActive = true;
    });
    builder.addCase(fetchLocations.fulfilled, (state, action) => {
      state.locations.isRequestActive = false;
      state.locations.locations = action.payload.locations;
    });
    builder.addCase(fetchLocationsWithDetails.pending, (state) => {
      state.locationsWithDetails.isRequestActive = true;
    });
    builder.addCase(fetchLocationsWithDetails.fulfilled, (state, action) => {
      state.locationsWithDetails.isRequestActive = false;
      state.locationsWithDetails.locations = action.payload.locations;
    });
    builder.addCase(fetchFirstUnusedAvailableRank.pending, (state) => {
      state.firstUnusedAvailableRank.isRequestActive = true;
    });
    builder.addCase(fetchFirstUnusedAvailableRank.fulfilled, (state, action) => {
      state.firstUnusedAvailableRank.isRequestActive = false;
      state.firstUnusedAvailableRank.firstUnusedAvailableRank = action.payload.firstUnusedRank;
    });
  },
});

export const fetchLocations = createAsyncThunk<IGetLocationsResponse, IGetLocationsRequest>(
  'locations/fetchLocations',
  async (request, thunkApi) => {
    if (request) {
      const client = Api.getLocationsClient(thunkApi.dispatch);
      const response = await client.getLocations(new GetLocationsRequest(request));

      if (
        ((thunkApi.getState() as any).docTypes as IDocTypesState).activeDocType?.primaryKey !==
          request.docTypePrimaryKey ||
        ((thunkApi.getState() as any).customers as ICustomersState).activeCustomer?.sysPrin.system !== request.system ||
        ((thunkApi.getState() as any).customers as ICustomersState).activeCustomer?.sysPrin.principle !==
          request.principle
      ) {
        throw new Error('Fetched locations do not correspond to the correct sys prin and doc type.');
      }

      return response;
    }

    return { locations: [] };
  },
);

export const fetchLocationsWithDetails = createAsyncThunk<
  IGetLocationsWithDetailsResponse,
  IGetLocationsWithDetailsRequest
>('locations/fetchLocationsWithDetails', async (request, thunkApi) => {
  if (request) {
    const client = Api.getLocationsClient(thunkApi.dispatch);
    const response = await client.getLocationsWithDetails(new GetLocationsWithDetailsRequest(request));

    return response;
  }

  return { locations: [] };
});

export const fetchFirstUnusedAvailableRank = createAsyncThunk<
  IGetFirstUnusedRankForLocationAndSysPrinResponse,
  IGetFirstUnusedRankForLocationAndSysPrinRequest
>('locations/FirstUnusedRankForLocationAndSysPrin', async (request, thunkApi) => {
  const client = Api.getLocationsClient(thunkApi.dispatch);
  const response = await client.getFirstUnusedRankForLocationAndSysPrin(
    new GetFirstUnusedRankForLocationAndSysPrinRequest(request),
  );
  return response;
});

export default locationsSlice.reducer;
